import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { GraduationCap, Search, SlidersHorizontal, Star, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filters, EMPTY_FILTERS } from "@/components/Filters";
import { EducationCard } from "@/components/EducationCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;
const FAV_KEY = "edutrazilica_favs";

const SORT_OPTIONS = [
  { value: "date_asc", label: "Najraniji datum" },
  { value: "date_desc", label: "Najkasniji datum" },
  { value: "deadline", label: "Rok prijave" },
  { value: "title", label: "Naziv (A–Ž)" },
];

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString("hr-HR", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function App() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("date_asc");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [showFavs, setShowFavs] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [favs, setFavs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const pollRef = useRef(null);

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  };

  const fetchFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/filters`);
      setFilterData(res.data);
    } catch (e) {
      console.error("filters error", e);
    }
  }, []);

  const buildParams = useCallback(
    (skip) => {
      const params = { sort, skip, limit: PAGE_SIZE };
      if (showFavs) {
        params.ids = favs.join(",");
        return params;
      }
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.grade) params.grade = filters.grade;
      if (filters.subject) params.subject = filters.subject;
      if (filters.county) params.county = filters.county;
      if (filters.category) params.category = filters.category;
      if (filters.organizer) params.organizer = filters.organizer;
      if (filters.modes.length) params.mode = filters.modes.join(",");
      if (filters.prices.length) params.price = filters.prices.join(",");
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      return params;
    },
    [filters, sort, showFavs, favs]
  );

  const fetchPage = useCallback(
    async (skip, append = false) => {
      if (showFavs && favs.length === 0) {
        setItems([]);
        setTotal(0);
        setLoading(false);
        return;
      }
      append ? setLoadingMore(true) : setLoading(true);
      try {
        const res = await axios.get(`${API}/educations`, { params: buildParams(skip) });
        setTotal(res.data.total);
        setItems((prev) => (append ? [...prev, ...res.data.items] : res.data.items));
      } catch (e) {
        console.error("educations error", e);
        toast.error("Greška pri dohvaćanju edukacija");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [buildParams, showFavs, favs.length]
  );

  useEffect(() => {
    fetchFilters();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [fetchFilters]);

  useEffect(() => {
    const t = setTimeout(() => fetchPage(0), 350);
    return () => clearTimeout(t);
  }, [fetchPage]);

  const startRefresh = async () => {
    try {
      await axios.post(`${API}/scrape/refresh`);
      setRefreshing(true);
      toast.info("Osvježavanje podataka pokrenuto…");
      pollRef.current = setInterval(async () => {
        const res = await axios.get(`${API}/scrape/status`);
        if (!res.data.running) {
          clearInterval(pollRef.current);
          setRefreshing(false);
          if (res.data.error) toast.error(`Greška: ${res.data.error}`);
          else toast.success(`Podaci osvježeni (${res.data.count} edukacija)`);
          fetchFilters();
          fetchPage(0);
        }
      }, 3000);
    } catch (e) {
      toast.error("Osvježavanje nije uspjelo");
    }
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const hasActiveFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  return (
    <div className="App min-h-screen bg-white">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-semibold text-slate-900 leading-tight">EduTražilica</h1>
              <p className="text-xs text-slate-500 hidden sm:block">Stručna usavršavanja za učitelje u RH</p>
            </div>
          </div>
          <Button
            data-testid="favorites-toggle-btn"
            variant={showFavs ? "default" : "outline"}
            onClick={() => setShowFavs((v) => !v)}
            className={`gap-2 ${showFavs ? "bg-blue-600 hover:bg-blue-700" : "text-slate-600"}`}
          >
            <Star className={`w-4 h-4 ${showFavs ? "fill-white" : ""}`} />
            <span className="hidden sm:inline">Spremljeno</span>
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{favs.length}</span>
          </Button>
        </div>
      </header>

      {/* Hero + search */}
      <section className="bg-gradient-to-b from-blue-50/80 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-2">
            Pronađite edukaciju za sebe
          </h2>
          <p className="text-sm sm:text-base text-slate-600 mb-6 max-w-2xl">
            Pretražite stručne skupove, seminare i webinare iz brojnih izvora — EttaEdu (AZOO), EMA (CARNET), FSO, HPKZ, HUROŠ, Školska knjiga, Profil Klett, Alfa i drugi.
          </p>
          <div className="relative max-w-3xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              data-testid="search-input"
              type="text"
              value={filters.q}
              onChange={(e) => {
                setShowFavs(false);
                setFilters({ ...filters, q: e.target.value });
              }}
              placeholder="Pretraži edukacije po nazivu, mjestu ili voditelju…"
              className="w-full h-14 pl-12 pr-4 rounded-xl border border-slate-300 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 text-base transition-colors bg-white shadow-sm"
            />
          </div>
        </div>
      </section>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block col-span-1">
            <Filters data={filterData} filters={filters} onChange={(f) => { setShowFavs(false); setFilters(f); }} onClear={clearFilters} />
          </aside>

          {/* Content */}
          <div className="col-span-1 lg:col-span-3 flex flex-col gap-6">
            {/* Results bar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {/* Mobile filter trigger */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button data-testid="mobile-filters-btn" variant="outline" className="lg:hidden gap-2 text-slate-600">
                      <SlidersHorizontal className="w-4 h-4" />
                      Filtri
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[320px] overflow-y-auto p-4">
                    <SheetHeader className="mb-2">
                      <SheetTitle className="font-heading text-left">Filtri</SheetTitle>
                    </SheetHeader>
                    <Filters data={filterData} filters={filters} onChange={(f) => { setShowFavs(false); setFilters(f); }} onClear={clearFilters} />
                  </SheetContent>
                </Sheet>
                <p data-testid="results-count" className="text-sm text-slate-600">
                  {showFavs ? (
                    <><span className="font-semibold text-slate-900">{total}</span> spremljenih edukacija</>
                  ) : (
                    <><span className="font-semibold text-slate-900">{total}</span> edukacija</>
                  )}
                </p>
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="w-[180px] bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Results */}
            {loading ? (
              <div data-testid="loading-state" className="flex flex-col items-center justify-center py-24 text-slate-400 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <p className="text-sm">Učitavanje edukacija…</p>
              </div>
            ) : items.length === 0 ? (
              <div data-testid="empty-state" className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="font-heading text-lg font-medium text-slate-800 mb-1">
                  {showFavs
                    ? "Nemate spremljenih edukacija"
                    : filters.organizer
                    ? "Nema aktivnih edukacija"
                    : "Nema rezultata"}
                </h3>
                <p className="text-sm text-slate-500 mb-5 max-w-sm">
                  {showFavs
                    ? "Kliknite na zvjezdicu na kartici edukacije kako biste je spremili."
                    : filters.organizer
                    ? `Organizator „${filters.organizer}" trenutno nema najavljenih predstojećih edukacija. Provjerite kasnije ili odaberite drugog organizatora.`
                    : "Pokušajte promijeniti ili ukloniti filtere kako biste pronašli edukacije."}
                </p>
                {!showFavs && hasActiveFilters && (
                  <Button data-testid="empty-clear-filters-btn" variant="outline" onClick={clearFilters} className="text-slate-600">
                    Očisti filtere
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div data-testid="results-grid" className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {items.map((edu) => (
                    <EducationCard key={edu.id} edu={edu} isFav={favs.includes(edu.id)} onToggleFav={toggleFav} />
                  ))}
                </div>
                {items.length < total && (
                  <div className="flex justify-center pt-2">
                    <Button
                      data-testid="load-more-btn"
                      variant="outline"
                      disabled={loadingMore}
                      onClick={() => fetchPage(items.length, true)}
                      className="gap-2 text-slate-600 px-8"
                    >
                      {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                      Učitaj još ({items.length}/{total})
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Izvori: <a href="https://ettaedu.azoo.hr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">EttaEdu</a>,{" "}
            <a href="https://ema.carnet.hr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">EMA</a>,{" "}
            <a href="https://edukacije.fso.hr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">FSO</a>,{" "}
            <a href="https://hpkz.hr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">HPKZ</a>,{" "}
            <a href="https://huros.hr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">HUROŠ</a>,{" "}
            <a href="https://cuc.carnet.hr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">CUC</a>,{" "}
            <a href="https://udzbenici.skolskaknjiga.hr/webinari/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Školska knjiga</a>,{" "}
            <a href="https://www.profil-klett.hr/edukacije" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Profil Klett</a>,{" "}
            <a href="https://kabinet.alfa.hr/edukacije" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Alfa</a>,{" "}
            <a href="https://ucitelji.hr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ucitelji.hr</a>
            {filterData.last_updated && <> · Zadnje ažurirano: {formatDate(filterData.last_updated)}</>}
          </p>
          <Button
            data-testid="refresh-data-btn"
            variant="ghost"
            size="sm"
            disabled={refreshing}
            onClick={startRefresh}
            className="gap-2 text-slate-500 hover:text-blue-600"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Osvježavanje…" : "Osvježi podatke"}
          </Button>
        </div>
      </footer>
    </div>
  );
}
